/**
 * IcgTemporalExplainer — explainer for ICG-At, the TEMPORAL Immunity
 * Closure Guarantee (governance name: TEMPORAL Safety Envelope).
 *
 * ICG-At pairs the PRECOMMIT Neutralization Theorem (primal) with the
 * REQUIRE Structural Duality Principle (dual) along the monad ⊣ comonad
 * graded adjunction F_κ ⊣ U_κ. The unit (commit) and counit (discharge)
 * keep production and demand structurally separate, so a commitment
 * cannot be retroactively manufactured.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import {
  AuditSurfaceDelta,
  CalloutBox,
  CounterModelGrid,
  ExplainerCard,
  SafetyEnvelopeDiagram,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import { COMPOSITIONALITY_PRINCIPLE_COLOR, THEOREM_COLOR } from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";

const PRIMAL_ACCENT = THEOREM_COLOR.PRECOMMIT;
const DUAL_ACCENT = COMPOSITIONALITY_PRINCIPLE_COLOR.REQUIRE.color;

const COUNTER_MODELS = [
  {
    title: "Primal only — PRECOMMIT, no REQUIRE",
    body: "A commitment cannot be forged inside one regime, but stacking commitment-bearing steps is unwitnessed: nothing certifies the composite is still a PRECOMMIT witness rather than a laundered one. The Kleisli phase can quietly leak into the co-Kleisli phase at the seam.",
  },
  {
    title: "Dual only — REQUIRE, no PRECOMMIT",
    body: "The monad ⊣ comonad duality composes demand-discharging steps, but with no precommitment neutralization there is no commitment to demand against — REQUIRE certifies an empty production phase.",
  },
] as const;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-icg-at",
  kind: "SSM · Immunity Closure Guarantee (S1⊗S2)",
  title: "ICG-At",
  subtitle: "TEMPORAL Safety Envelope · PRECOMMIT ⊗ REQUIRE",
  accent: PRIMAL_ACCENT,
  accentAlt: DUAL_ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  {
    label: "PRECOMMIT (primal NT)",
    href: "#explainer-precommit",
    accent: PRIMAL_ACCENT,
  },
  { label: "REQUIRE (dual SDP)", href: "#explainer-require", accent: DUAL_ACCENT },
  { label: "Graded monad", href: "#explainer-graded-monad" },
  { label: "Comonad", href: "#explainer-comonad" },
];

const ENVELOPE = (
  <SafetyEnvelopeDiagram
    icg="ICG-At"
    primalLabel="PRECOMMIT"
    primalKind="Neutralization Theorem · W"
    primalAccent={PRIMAL_ACCENT}
    dualLabel="REQUIRE"
    dualKind="Structural Duality Principle · At"
    dualAccent={DUAL_ACCENT}
    adjunction="monad ⊣ comonad (F_κ ⊣ U_κ)"
    unitRole="commit"
    counitRole="discharge"
  />
);

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      <strong>ICG-At</strong> is the TEMPORAL Safety Envelope. It seals precommitment neutralization
      (PRECOMMIT) and its compositional dual (REQUIRE) into one two-sided certificate that keeps
      production and demand structurally separate.
    </p>
  ),
  intuition: (
    <>
      <p>
        PRECOMMIT makes retroactive commitment unconstructable: you cannot manufacture a witness
        after the effect it was supposed to license. REQUIRE certifies that the monad ↔ comonad
        duality preserves that guarantee when commitment-bearing steps are stacked.
      </p>
      <p>
        Held separately, the two theorems are sound about different items. A confused-deputy attack
        can satisfy the precommit check on one trace fragment and the require check on another, and
        the audit never proves the two fragments are the same crossing.
      </p>
      <p>
        ICG-At is the single graded adjunction that contains both sides. Its commit and discharge
        operations are the structural proof that production and demand are complementary — the
        envelope is <strong>strictly stronger</strong> than holding the theorem and the principle
        apart.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        ICG-At is the graded adjunction <MathInline>{tex`F_{\kappa} \dashv U_{\kappa}`}</MathInline>
        , which contains the commitment monad{" "}
        <MathInline>{tex`T_{\kappa} = U_{\kappa} \circ F_{\kappa}`}</MathInline> and the demand
        comonad <MathInline>{tex`D_{\kappa} = F_{\kappa} \circ U_{\kappa}`}</MathInline>. Its two
        structural witnesses are:
      </p>
      <ul>
        <li>
          the <strong>unit η</strong> — <em>commit</em>: the WITNESS phase, a precommitment produced
          before the effect it licenses;
        </li>
        <li>
          the <strong>counit ε</strong> — <em>discharge</em>: the ACCOUNT phase that settles the
          demand against the commitment.
        </li>
      </ul>
      <p>
        The triangle identities force commit-then-discharge to the identity, so the Kleisli
        (production) and co-Kleisli (demand) categories cannot collapse — a commitment cannot be
        manufactured after the fact, and a discharge cannot launder back into a precommit. This is
        the Seam Normal Form non-feedback invariant typed at the SDP level.
      </p>
      <AuditSurfaceDelta
        primalCheck="PRECOMMIT witness"
        dualCheck="REQUIRE witness"
        envelopeLabel="ICG-At envelope"
        primalAccent={PRIMAL_ACCENT}
        dualAccent={DUAL_ACCENT}
      />
      <p style={{ marginTop: "0.5rem" }}>
        Categorical form: ICG-At is the <em>Adjunction Certificate</em> whose left adjoint is the
        commitment monad and whose right adjoint is the demand comonad — the unit and counit are the
        certificate that the two phases are mutual sections.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        <strong>Why "strictly stronger".</strong> Each side alone admits a counter-model the
        envelope rules out:
      </p>
      <CounterModelGrid counterModels={COUNTER_MODELS} ariaLabel="ICG-At counter-models" />
      <p style={{ marginTop: "0.75rem" }}>
        ICG-At makes the forbidden generator <strong>G3 (time reversal)</strong> structurally
        absent: the graded-adjunction boundary denies production witnesses laundering into demand
        discharges. The envelope enforces axioms A2 (Witness-Before-Effect) and A3 (Phase
        Separation) at once.
      </p>
      <p>
        <strong>Worked adversary.</strong> An actor issues an effect, then fabricates a
        precommitment "dated before" the effect so that an audit sees a well-ordered commit-then-act
        sequence; or accounting evidence (an ACCOUNT phase output) is fed back to manufacture an
        authorization. Under ICG-At the unit and counit live in distinct phases tied by the triangle
        identities — a commit that follows its own effect, or a discharge that re-emerges as a
        commit, is structurally inexpressible.
      </p>
      <p>
        <strong>Theorem-collision check.</strong> Bypassing ICG-At means bypassing PRECOMMIT and
        REQUIRE at once, or escaping the graded carrier — both leave the kernel category and vacate
        the guarantee rather than violate it.
      </p>
      <CalloutBox eyebrow="Safety envelope: ICG-At" accent={DUAL_ACCENT}>
        PRECOMMIT (NT) ⊗ REQUIRE (SDP) = ICG-At. The audit surface collapses from an NT witness plus
        an SDP witness to one envelope carrying the graded-adjunction unit and counit.
      </CalloutBox>
    </>
  ),
  references: [
    {
      label: "Theorem Canon — Immunity Closure Guarantees (§7a)",
      path: "docs/reference/Theorem-Canon.md",
    },
    {
      label: "RD-97 — Immunity Closure Guarantees",
      path: "research/settled/RD-97-BI-GUARANTEES-TWO-SIDED-SAFETY.md",
      note: "Canonical source for the seven-envelope algebra and the naming triad.",
    },
    {
      label: "Safety Envelope foundations",
      path: "theory/structural/Immunity-Closure-Guarantees/00-Safety-Envelope-Foundations.md",
    },
    {
      label: "ICG-At theory document",
      path: "theory/structural/Immunity-Closure-Guarantees/04-ICG-At-Precommit-And-Require.md",
    },
    {
      label: "Immunity Closure Guarantee (Agda)",
      path: "agda/Research/BiGuarantee/BiTemporal.agda",
    },
  ],
};

export function IcgTemporalExplainer(): React.JSX.Element {
  return (
    <ExplainerCard identity={IDENTITY} tiers={TIERS} diagram={ENVELOPE} crossLinks={CROSS_LINKS} />
  );
}
